<template>
  <div class="multiple-discounts">
    <div class="add-discount-section">
      <div class="discount-input-row">
        <div class="discount-type-label">Korting</div>
        <form-input
          id="discount-percentage-description"
          v-model="newPercentageDiscount.description"
          placeholder="omschrijving"
          class="description-input"
          title=""
          :is-read-only="disabled"
        />
        <div class="tax-options" />

        <span class="currency-symbol">%</span>
        <form-input
          id="discount-percentage"
          v-model="newPercentageDiscount.value"
          placeholder="percentage"
          type="number"
          class="value-input"
          title=""
          :is-read-only="disabled"
        />
        <button
          type="button"
          class="add-button"
          :disabled="disabled || !canAddPercentageDiscount"
          @click="addPercentageDiscount"
        >
          Toevoegen
        </button>
      </div>

      <div class="discount-input-row">
        <div class="discount-type-label">Korting</div>
        <form-input
          id="discount-amount-description"
          v-model="newAmountDiscount.description"
          placeholder="omschrijving"
          class="description-input"
          title=""
          :is-read-only="disabled"
        />
        <div class="tax-options">
          <label class="radio-option">
            <input
              v-model="newAmountDiscount.taxType"
              type="radio"
              value="excl"
              :disabled="disabled"
            />
            <span class="radio-label">excl.</span>
          </label>
          <label class="radio-option">
            <input
              v-model="newAmountDiscount.taxType"
              type="radio"
              value="incl"
              :disabled="disabled"
            />
            <span class="radio-label">incl.</span>
          </label>
        </div>
        <span class="currency-symbol">€</span>
        <form-input
          id="discount-amount"
          v-model="newAmountDiscount.value"
          placeholder="bedrag"
          type="number"
          class="value-input"
          title=""
          :is-read-only="disabled"
        />
        <button
          type="button"
          class="add-button"
          :disabled="disabled || !canAddAmountDiscount"
          @click="addAmountDiscount"
        >
          Toevoegen
        </button>
      </div>
    </div>

    <!-- Applied Discounts List -->
    <div v-if="appliedDiscounts.length > 0" class="applied-discounts">
      <div
        v-for="(discount, index) in appliedDiscounts"
        :key="discount.id"
        class="applied-discount-item"
      >
        <span class="discount-description">{{ discount.description }}</span>
        <span v-if="discount.type === 'amount'" class="discount-tax-type"
          >{{ discount.taxType }}.</span
        >
        <span class="discount-value">
          <span v-if="discount.type === 'amount'">€</span>
          <span v-else>%</span>
          {{ formatDiscountValue(discount.value) }}
        </span>
        <button
          type="button"
          class="edit-button"
          :disabled="disabled"
          :title="i18n.t('common/edit')"
          @click="editDiscount(index)"
        >
          <icon icon="pencil" />
        </button>
        <button
          type="button"
          class="delete-button"
          :disabled="disabled"
          :title="i18n.t('common/delete')"
          @click="deleteDiscount(index)"
        >
          <icon icon="bin" />
        </button>
      </div>
    </div>

    <div v-if="editingDiscount" class="edit-modal-overlay" @click="cancelEdit">
      <div class="edit-modal" @click.stop>
        <h3>Korting bewerken</h3>
        <div class="edit-form">
          <form-input
            id="discount-edit-description"
            v-model="editingDiscount.description"
            label="Omschrijving"
            class="description-input"
            title=""
          />
          <div v-if="editingDiscount.type === 'amount'" class="tax-options">
            <label class="radio-option">
              <input v-model="editingDiscount.taxType" type="radio" value="excl" />
              <span class="radio-label">excl.</span>
            </label>
            <label class="radio-option">
              <input v-model="editingDiscount.taxType" type="radio" value="incl" />
              <span class="radio-label">incl.</span>
            </label>
          </div>
          <div class="value-input-group">
            <span class="currency-symbol">
              <span v-if="editingDiscount.type === 'amount'">€</span>
              <span v-else>%</span>
            </span>
            <form-input
              id="discount-edit-amount"
              v-model="editingDiscount.value"
              :label="editingDiscount.type === 'amount' ? 'Bedrag' : 'Percentage'"
              type="number"
              class="value-input"
              title=""
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="cancel-button" @click="cancelEdit">Annuleren</button>
          <button type="button" class="save-button" @click="saveEdit">Opslaan</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'

export default {
  name: 'MultipleDiscountsComponent',
  components: {
    FormInput,
    Icon,
  },
  props: {
    discounts: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:discounts', 'discountsChanged'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      appliedDiscounts: [...this.discounts],
      newPercentageDiscount: {
        description: '',
        taxType: 'excl',
        value: '',
        type: 'percentage',
      },
      newAmountDiscount: {
        description: '',
        taxType: 'excl',
        value: '',
        type: 'amount',
      },
      editingDiscount: null,
      editingIndex: -1,
      nextId: 1,
    }
  },
  computed: {
    canAddPercentageDiscount() {
      return (
        this.newPercentageDiscount.description.trim() !== '' &&
        this.newPercentageDiscount.value !== '' &&
        !isNaN(parseFloat(this.newPercentageDiscount.value)) &&
        parseFloat(this.newPercentageDiscount.value) > 0
      )
    },
    canAddAmountDiscount() {
      return (
        this.newAmountDiscount.description.trim() !== '' &&
        this.newAmountDiscount.value !== '' &&
        !isNaN(parseFloat(this.newAmountDiscount.value)) &&
        parseFloat(this.newAmountDiscount.value) > 0
      )
    },
  },
  watch: {
    discounts: {
      handler(newDiscounts) {
        this.appliedDiscounts = [...newDiscounts]
      },
      deep: true,
    },
  },
  mounted() {
    // Initialize nextId based on existing discounts
    if (this.appliedDiscounts.length > 0) {
      this.nextId = Math.max(...this.appliedDiscounts.map((d) => d.id || 0)) + 1
    }
  },
  methods: {
    addPercentageDiscount() {
      if (!this.canAddPercentageDiscount) return

      const discount = {
        id: this.nextId++,
        description: this.newPercentageDiscount.description.trim(),
        taxType: this.newPercentageDiscount.taxType,
        value: parseFloat(this.newPercentageDiscount.value),
        type: 'percentage',
      }

      this.appliedDiscounts.push(discount)
      this.resetPercentageForm()
      this.emitChanges()
    },
    addAmountDiscount() {
      if (!this.canAddAmountDiscount) return

      const discount = {
        id: this.nextId++,
        description: this.newAmountDiscount.description.trim(),
        taxType: this.newAmountDiscount.taxType,
        value: parseFloat(this.newAmountDiscount.value),
        type: 'amount',
      }

      this.appliedDiscounts.push(discount)
      this.resetAmountForm()
      this.emitChanges()
    },
    resetPercentageForm() {
      this.newPercentageDiscount = {
        description: '',
        taxType: 'excl',
        value: '',
        type: 'percentage',
      }
    },
    resetAmountForm() {
      this.newAmountDiscount = {
        description: '',
        taxType: 'excl',
        value: '',
        type: 'amount',
      }
    },
    editDiscount(index) {
      this.editingIndex = index
      this.editingDiscount = { ...this.appliedDiscounts[index] }
    },
    saveEdit() {
      if (this.editingIndex >= 0 && this.editingDiscount) {
        this.appliedDiscounts[this.editingIndex] = { ...this.editingDiscount }
        this.cancelEdit()
        this.emitChanges()
      }
    },
    cancelEdit() {
      this.editingDiscount = null
      this.editingIndex = -1
    },
    deleteDiscount(index) {
      this.appliedDiscounts.splice(index, 1)
      this.emitChanges()
    },
    formatDiscountValue(value) {
      return new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    },
    emitChanges() {
      this.$emit('update:discounts', this.appliedDiscounts)
      this.$emit('discountsChanged', this.appliedDiscounts)
    },
  },
}
</script>
<style lang="scss" scoped>
.multiple-discounts {
  .add-discount-section {
    margin-bottom: 1rem;
  }

  .discount-input-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background-color: #fff;

    .discount-type-label {
      font-weight: bold;
      min-width: 60px;
      color: #333;
    }

    .description-input {
      flex: 2;
      min-width: 150px;
    }

    .tax-options {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      min-width: 135px;

      .radio-option {
        display: flex;
        align-items: center;
        cursor: pointer;

        input[type='radio'] {
          margin-right: 0.25rem;
        }

        .radio-label {
          font-size: 0.875rem;
          margin-right: 0.5rem;
        }
      }
    }

    .currency-symbol {
      font-weight: bold;
      color: #333;
      min-width: 20px;
      text-align: center;
    }

    .value-input {
      flex: 1;
      min-width: 100px;
    }

    .add-button {
      background-color: #007bff;
      color: #fff;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
      min-width: 100px;
      transition: background-color 0.2s ease;

      &:hover:not(:disabled) {
        background-color: #0056b3;
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }

  .applied-discounts {
    margin-top: 1rem;

    .applied-discount-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      margin-bottom: 0.5rem;
      background-color: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 4px;

      .discount-description {
        flex: 1;
        font-weight: 500;
        color: #333;
      }

      .discount-tax-type {
        color: #666;
        font-size: 0.875rem;
      }

      .discount-value {
        font-weight: bold;
        color: #333;
        min-width: 80px;
        text-align: right;
      }

      .edit-button,
      .delete-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 4px;
        transition: background-color 0.2s ease;

        :deep(.icon) {
          width: 16px;
          height: 16px;
        }

        &:hover:not(:disabled) {
          background-color: #f0f0f0;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      .edit-button {
        color: #007bff;
      }

      .delete-button {
        color: #dc3545;
      }
    }
  }

  .edit-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .edit-modal {
    background: #fff;
    border-radius: 8px;
    padding: 1.5rem;
    min-width: 400px;
    max-width: 90vw;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

    h3 {
      margin: 0 0 1rem 0;
      color: #333;
      font-size: 1.25rem;
    }

    .edit-form {
      margin-bottom: 1.5rem;

      .description-input {
        margin-bottom: 1rem;
      }

      .tax-options {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;

        .radio-option {
          display: flex;
          align-items: center;
          cursor: pointer;

          input[type='radio'] {
            margin-right: 0.25rem;
          }

          .radio-label {
            margin-right: 1rem;
          }
        }
      }

      .value-input-group {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .currency-symbol {
          font-weight: bold;
          color: #333;
        }

        .value-input {
          flex: 1;
        }
      }
    }

    .modal-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;

      .cancel-button,
      .save-button {
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        border: 1px solid;
        transition: all 0.2s ease;
      }

      .cancel-button {
        background: #fff;
        color: #666;
        border-color: #ccc;

        &:hover {
          background-color: #f8f9fa;
        }
      }

      .save-button {
        background: #007bff;
        color: #fff;
        border-color: #007bff;

        &:hover {
          background-color: #0056b3;
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .multiple-discounts {
    .discount-input-row {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;

      .discount-type-label {
        min-width: auto;
      }

      .tax-options {
        justify-content: flex-start;
        min-width: auto;

        .radio-option {
          .radio-label {
            margin-right: 0.5rem;
          }
        }
      }

      .currency-symbol {
        min-width: auto;
        text-align: left;
      }

      .value-input {
        min-width: auto;
      }

      .add-button {
        min-width: auto;
      }
    }

    .applied-discount-item {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;

      .discount-value {
        text-align: left;
        min-width: auto;
      }

      .edit-button,
      .delete-button {
        align-self: flex-end;
      }
    }
  }
}
</style>
